/**
 * App theme (light / dark / system). The chosen preference is stored in
 * localStorage and applied as a `dark` class on <html> (Tailwind darkMode:
 * 'class' + the dark override layer in styles.css). index.html applies the
 * stored preference inline before first paint to avoid a light flash.
 */

export type ThemePreference = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'ss-theme';
const META_LIGHT = '#0d9488';
const META_DARK = '#0f172a';

export function getThemePreference(): ThemePreference {
  const v = localStorage.getItem(STORAGE_KEY);
  return v === 'light' || v === 'dark' ? v : 'system';
}

export function setThemePreference(pref: ThemePreference) {
  if (pref === 'system') localStorage.removeItem(STORAGE_KEY);
  else localStorage.setItem(STORAGE_KEY, pref);
  applyTheme();
}

function resolveDark(pref: ThemePreference): boolean {
  if (pref === 'dark') return true;
  if (pref === 'light') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function applyTheme() {
  const dark = resolveDark(getThemePreference());
  document.documentElement.classList.toggle('dark', dark);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', dark ? META_DARK : META_LIGHT);
}

/** Call once at boot: applies the theme and follows OS changes in system mode. */
export function initTheme() {
  applyTheme();
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (getThemePreference() === 'system') applyTheme();
  });
}
